// Validates Swedish bank account numbers according to Bankgirot's specification
// "Bankernas kontonummeruppbyggnad" (2024-02-22).
//
// Unlike the legacy `kontonummer` validator (./kontonummer.js), which checks the
// clearing number and account number glued together as one string, this one
// validates them as SEPARATE fields — the same way they are stored and sent to
// the bank. Validating the concatenation lets the boundary between the two
// fields drift (e.g. a 4-digit clearing + 11-digit account would pass while the
// bank rejects an account longer than 10 digits).
//
// This mirrors the server-side validation in apply-bank
// (internal/account/validate.go) so the form and the payment backend agree on
// exactly which numbers are valid.
//
// Each clearing range maps to an account type (1 or 2) and a check method:
//   type 1 — the clearing number participates in the mod-11 check
//     comment 1: clearing digits 2-4 + 7-digit account (skip first clearing digit)
//     comment 2: full 4-digit clearing + 7-digit account
//   type 2 — the clearing number is NOT part of the check
//     comment 1: 10-digit account, mod-10
//     comment 2: 9-digit account, mod-11 (Handelsbanken)
//     comment 3: up to 10-digit account, mod-10 (Swedbank 8xxx, Plusgirot)

const TYPE1 = 1;
const TYPE2 = 2;

const T1_COMMENT1 = "t1c1";
const T1_COMMENT2 = "t1c2";
const T2_COMMENT1 = "t2c1";
const T2_COMMENT2 = "t2c2";
const T2_COMMENT3 = "t2c3";

// Complete clearing-code registry from the Bankgirot specification, sorted by
// clearing range. Lookup is linear (small table).
const rules = [
  // Type 1 banks
  { from: 1100, to: 1199, type: TYPE1, method: T1_COMMENT1, bank: "Nordea" },
  { from: 1200, to: 1399, type: TYPE1, method: T1_COMMENT1, bank: "Danske Bank" },
  { from: 1400, to: 2099, type: TYPE1, method: T1_COMMENT1, bank: "Nordea" },
  { from: 2300, to: 2399, type: TYPE1, method: T1_COMMENT2, bank: "Ålandsbanken" },
  { from: 2400, to: 2499, type: TYPE1, method: T1_COMMENT1, bank: "Danske Bank" },
  { from: 3000, to: 3299, type: TYPE1, method: T1_COMMENT1, bank: "Nordea" }, // excl personkonto 3300
  { from: 3301, to: 3399, type: TYPE1, method: T1_COMMENT1, bank: "Nordea" }, // excl personkonto 3300
  { from: 3400, to: 3409, type: TYPE1, method: T1_COMMENT1, bank: "Länsförsäkringar Bank" },
  { from: 3410, to: 3781, type: TYPE1, method: T1_COMMENT1, bank: "Nordea" }, // excl personkonto 3782
  { from: 3783, to: 3999, type: TYPE1, method: T1_COMMENT1, bank: "Nordea" },
  { from: 4000, to: 4999, type: TYPE1, method: T1_COMMENT2, bank: "Nordea" },
  { from: 5000, to: 5999, type: TYPE1, method: T1_COMMENT1, bank: "SEB" },
  { from: 7000, to: 7999, type: TYPE1, method: T1_COMMENT1, bank: "Swedbank" },
  { from: 9020, to: 9029, type: TYPE1, method: T1_COMMENT2, bank: "Länsförsäkringar Bank" },
  { from: 9040, to: 9049, type: TYPE1, method: T1_COMMENT2, bank: "Citibank" },
  { from: 9060, to: 9069, type: TYPE1, method: T1_COMMENT1, bank: "Länsförsäkringar Bank" },
  { from: 9070, to: 9079, type: TYPE1, method: T1_COMMENT1, bank: "Multitude Bank" },
  { from: 9100, to: 9109, type: TYPE1, method: T1_COMMENT2, bank: "Nordnet Bank" },
  { from: 9120, to: 9124, type: TYPE1, method: T1_COMMENT1, bank: "SEB" },
  { from: 9130, to: 9149, type: TYPE1, method: T1_COMMENT1, bank: "SEB" },
  { from: 9150, to: 9169, type: TYPE1, method: T1_COMMENT2, bank: "Skandiabanken" },
  { from: 9170, to: 9179, type: TYPE1, method: T1_COMMENT1, bank: "IKANO Bank" },
  { from: 9190, to: 9199, type: TYPE1, method: T1_COMMENT2, bank: "DNB Bank" },
  { from: 9230, to: 9239, type: TYPE1, method: T1_COMMENT1, bank: "Marginalen Bank" },
  { from: 9250, to: 9259, type: TYPE1, method: T1_COMMENT1, bank: "SBAB" },
  { from: 9260, to: 9269, type: TYPE1, method: T1_COMMENT2, bank: "DNB Bank" },
  { from: 9270, to: 9279, type: TYPE1, method: T1_COMMENT1, bank: "ICA Banken" },
  { from: 9280, to: 9289, type: TYPE1, method: T1_COMMENT1, bank: "Resurs Bank" },
  { from: 9390, to: 9399, type: TYPE1, method: T1_COMMENT2, bank: "Landshypotek" },
  { from: 9460, to: 9469, type: TYPE1, method: T1_COMMENT1, bank: "Santander Consumer Bank" },
  { from: 9470, to: 9479, type: TYPE1, method: T1_COMMENT2, bank: "BNP Paribas" },
  { from: 9550, to: 9569, type: TYPE1, method: T1_COMMENT2, bank: "Avanza Bank" },
  { from: 9580, to: 9589, type: TYPE1, method: T1_COMMENT1, bank: "Aion Bank" },
  { from: 9590, to: 9599, type: TYPE1, method: T1_COMMENT2, bank: "Erik Penser" },
  { from: 9630, to: 9639, type: TYPE1, method: T1_COMMENT1, bank: "Lån & Spar Bank" },
  { from: 9640, to: 9649, type: TYPE1, method: T1_COMMENT2, bank: "NOBA Bank" },
  { from: 9660, to: 9669, type: TYPE1, method: T1_COMMENT2, bank: "Svea Bank" },
  { from: 9670, to: 9679, type: TYPE1, method: T1_COMMENT2, bank: "JAK Medlemsbank" },
  { from: 9680, to: 9689, type: TYPE1, method: T1_COMMENT1, bank: "BlueStep Finans" },
  { from: 9700, to: 9709, type: TYPE1, method: T1_COMMENT2, bank: "Ekobanken" },
  { from: 9710, to: 9719, type: TYPE1, method: T1_COMMENT2, bank: "Lunar Bank" },
  { from: 9750, to: 9759, type: TYPE1, method: T1_COMMENT2, bank: "Northmill Bank" },
  { from: 9780, to: 9789, type: TYPE1, method: T1_COMMENT2, bank: "Klarna Bank" },
  { from: 9880, to: 9889, type: TYPE1, method: T1_COMMENT2, bank: "Riksgälden" },

  // Type 2 banks
  { from: 3300, to: 3300, type: TYPE2, method: T2_COMMENT1, bank: "Nordea" }, // personkonto
  { from: 3782, to: 3782, type: TYPE2, method: T2_COMMENT1, bank: "Nordea" }, // personkonto
  { from: 6000, to: 6999, type: TYPE2, method: T2_COMMENT2, bank: "Handelsbanken" }, // mod-11 on 9 digits
  { from: 8000, to: 8999, type: TYPE2, method: T2_COMMENT3, bank: "Swedbank" },
  { from: 9180, to: 9189, type: TYPE2, method: T2_COMMENT1, bank: "Danske Bank" },
  { from: 9300, to: 9349, type: TYPE2, method: T2_COMMENT1, bank: "Swedbank" }, // f.d. Sparbanken Öresund
  { from: 9500, to: 9549, type: TYPE2, method: T2_COMMENT3, bank: "Nordea/Plusgirot" },
  { from: 9570, to: 9579, type: TYPE2, method: T2_COMMENT1, bank: "Sparbanken Syd" },
  { from: 9890, to: 9899, type: TYPE2, method: T2_COMMENT1, bank: "Riksgälden" },
  { from: 9960, to: 9969, type: TYPE2, method: T2_COMMENT3, bank: "Nordea/Plusgirot" },
];

function findRule(code) {
  return rules.find((r) => code >= r.from && code <= r.to);
}

// Strips spaces and hyphens. Returns null if anything other than digits remains.
function cleanDigits(input) {
  const stripped = String(input ?? "").replace(/[\s-]/g, "");
  return /^[0-9]*$/.test(stripped) ? stripped : null;
}

function padLeft(s, length) {
  return s.length >= length ? s : "0".repeat(length - s.length) + s;
}

// 10-modulmetoden (Luhn). Weights alternate 1,2,1,2,... from the right.
function checkMod10(digits) {
  let sum = 0;
  const n = digits.length;
  for (let i = 0; i < n; i++) {
    let d = digits.charCodeAt(i) - 48;
    const posFromRight = n - 1 - i;
    if (posFromRight % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return sum % 10 === 0;
}

// 11-modulmetoden. Weights 1,2,3,...,10 from the right, then repeating.
function checkMod11(digits) {
  let sum = 0;
  const n = digits.length;
  for (let i = 0; i < n; i++) {
    const d = digits.charCodeAt(i) - 48;
    const posFromRight = n - 1 - i;
    const weight = (posFromRight % 10) + 1;
    sum += d * weight;
  }
  return sum % 11 === 0;
}

// Type 1 — mod-11 over (part of) the clearing number plus a 7-digit account.
function validateType1(clearing, account, method) {
  account = padLeft(account, 7);
  if (account.length > 7) return false; // account must be at most 7 digits

  const digits =
    method === T1_COMMENT1
      ? clearing.slice(1, 4) + account // skip first clearing digit
      : clearing + account; // full 4-digit clearing
  return checkMod11(digits);
}

// Type 2 — the clearing number is excluded; only the account is checked.
function validateType2(account, method) {
  switch (method) {
    case T2_COMMENT1: // 10 digits, mod-10
      account = padLeft(account, 10);
      return account.length <= 10 && checkMod10(account);
    case T2_COMMENT2: // Handelsbanken: 9 digits, mod-11
      account = padLeft(account, 9);
      return account.length <= 9 && checkMod11(account);
    case T2_COMMENT3: // up to 10 digits, mod-10
      account = padLeft(account, 10);
      return account.length <= 10 && checkMod10(account);
    default:
      return false;
  }
}

/**
 * Validates a Swedish bank account number, checking the clearing number and the
 * account number as separate fields.
 *
 * @param {String} clearing Clearing number (4 digits, or 5 for Swedbank 8xxx).
 * @param {String} account  Account number (without the clearing number).
 * @returns {Boolean} true if the clearing/account pair is valid.
 */
export function validateAccountNumber(clearing, account) {
  clearing = cleanDigits(clearing);
  account = cleanDigits(account);
  if (clearing === null || account === null) return false;
  if (clearing.length < 4) return false;

  // For 5-digit Swedbank clearing (8xxxx) the first 4 digits identify the rule.
  const rule = findRule(parseInt(clearing.slice(0, 4), 10));
  if (!rule) return false;

  return rule.type === TYPE1
    ? validateType1(clearing.slice(0, 4), account, rule.method)
    : validateType2(account, rule.method);
}

/**
 * Returns the bank name for a clearing number, or null if the clearing is not
 * recognized (too short, non-numeric, or outside every known range).
 *
 * @param {String} clearing Clearing number (4 digits, or 5 for Swedbank 8xxx).
 * @returns {String|null}
 */
export function bankName(clearing) {
  clearing = cleanDigits(clearing);
  if (clearing === null || clearing.length < 4) return null;
  const rule = findRule(parseInt(clearing.slice(0, 4), 10));
  return rule ? rule.bank : null;
}
